import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Avatar, Box, IconButton, Toolbar, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import UserMenu from './UserMenu';

const ringSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const AnimatedIconButton = styled(IconButton)(() => ({
  position: 'relative',
  borderRadius: '50%',
  width: 48,
  height: 48,
  padding: 0,
  color: '#ffffff',
  backgroundColor: '#000000',
  overflow: 'hidden',
  transition: 'background-color 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: 2,
    background: 'conic-gradient(from 0deg, #0ea5e9, #00a76f, #0ea5e9)',
    animation: `${ringSpin} 2.8s linear infinite`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none'
  },
  '&:hover': {
    backgroundColor: '#3a3939ff'
  },
  '& > *': {
    position: 'relative',
    zIndex: 1
  }
}));

const colorFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  60% { opacity: 1; transform: translateY(0); }
  100% { opacity: 1; transform: translateY(0); }
`;

const AnimatedTitle = styled(Typography)(() => ({
  fontWeight: 700,
  letterSpacing: '-0.01em',
  backgroundImage:
    'linear-gradient(120deg, #10b981 0%, #0ea5e9 45%, #22d3ee 60%, #10b981 100%)',
  backgroundSize: '200% auto',
  color: 'transparent',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  animation: `${colorFlow} 5s ease infinite, ${fadeIn} 900ms ease forwards`,
  display: 'inline-block',
  position: 'relative',
  opacity: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -6,
    width: '100%',
    height: 3,
    borderRadius: 999,
    background:
      'linear-gradient(120deg, rgba(16,185,129,0.4), rgba(14,165,233,0.5) 50%, rgba(34,211,238,0.3))',
    filter: 'blur(0.5px)',
    animation: `${colorFlow} 5s ease infinite, ${fadeIn} 900ms ease forwards`
  }
}));

const Header = ({ drawerWidth, onToggleSidebar }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const user = useSelector((state) => state.auth.user);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'AD';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="inherit"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        backgroundColor: 'rgba(248, 250, 252, 0.78)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.12)',        
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: -1,
          height: 2,
          borderRadius: 999,
          background:
            'linear-gradient(90deg, rgba(34,211,238,0), rgba(34,211,238,0.45) 40%, rgba(16,185,129,0.45) 60%, rgba(16,185,129,0))',
          boxShadow: '0 0 18px rgba(34, 211, 238, 0.25)',
          pointerEvents: 'none',
          zIndex: 0
        }
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 4 },
          minHeight: 72,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={onToggleSidebar}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <AnimatedTitle variant="h6" sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, whiteSpace: 'nowrap' }}>
            MRT Ticket Engine
          </AnimatedTitle>
        </Box>
        <AnimatedIconButton onClick={handleOpenMenu}>
          <Avatar
            sx={{
              bgcolor: 'transparent',
              color: '#ffffff',
              width: 40,
              height: 40,
              fontWeight: 600
            }}
          >
            {initials}
          </Avatar>
        </AnimatedIconButton>
        <UserMenu anchorEl={anchorEl} onClose={handleCloseMenu} />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
